import { Suspense, lazy, useCallback, useRef, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { TransitionLink } from '../lib/pageTransition';

import IdentityStrip from '../components/IdentityStrip';
import HeroField from '../components/HeroField';
import SectionHead, { sectionHeadingId } from '../components/SectionHead';
import ShowcaseTile from '../components/ShowcaseTile';
import ProjectSheet from '../components/ProjectSheet';
import Arrow from '../components/Arrow';
import Testimonial from '../components/Testimonial';
import { getProjectBySlug, projectPage, projects } from '../data/projects';
import { featuredMedia } from '../data/mediaItems';
import { posts } from '../data/posts';
import { typeOf } from '../data/projectFilters';

// The lightbox only loads once a photo is opened.
const Lightbox = lazy(() =>
  Promise.all([
    import('yet-another-react-lightbox'),
    import('yet-another-react-lightbox/styles.css'),
  ]).then(([m]) => m)
);

const LEAD = ['observatory', 'ghosts-playground', 'mixvault'];
const AT_WORK = ['cn-integration', '360-ops', 'azure-pipelines', 'urbaneyes'];

const pick = (slugs) => slugs.map(getProjectBySlug).filter(Boolean);

function Home() {
  const reduce = useReducedMotion();
  const lead = pick(LEAD);
  const atWork = pick(AT_WORK);
  const clients = projects.filter((project) => typeOf(project) === 'clients');
  const featuredQuote = clients.find((project) => project.quote?.featured)?.quote;
  const photos = featuredMedia.slice(0, 3);
  const latest = posts.slice(0, 3);
  const [active, setActive] = useState(null);
  const [photo, setPhoto] = useState(-1);
  const opener = useRef(null);

  const open = (project) => {
    opener.current = document.activeElement;
    setActive(project);
  };

  const close = useCallback(() => {
    setActive(null);
    requestAnimationFrame(() => opener.current?.focus?.());
  }, []);

  // ghosts-playground's tile runs a small live field instead of a screenshot.
  const liveTile = (project) =>
    project.slug === 'ghosts-playground' ? (
      <HeroField
        reduce={Boolean(reduce)}
        intensity={1.1}
        rosette={0}
        dprCap={1}
        className="tile-field"
      />
    ) : undefined;

  return (
    <div className="home-page">
      <IdentityStrip />

      <section className="home-section" aria-labelledby={sectionHeadingId('/projects')}>
        <SectionHead to="/projects" linkLabel="All work" />

        <div className="home-lead">
          {lead.map((project, i) => (
            <ShowcaseTile
              key={project.slug}
              title={project.title}
              subtitle={[project.status, project.tag].filter(Boolean).join(' · ')}
              line={project.line}
              img={project.img}
              field={liveTile(project)}
              fit={project.imgFit}
              size={i === 0 ? 'large' : 'default'}
              href={projectPage(project)}
              onClick={() => open(project)}
            />
          ))}
        </div>

        <div className="home-work">
          <h3 className="home-work-label">Professional</h3>
          <ul className="home-work-list">
            {atWork.map((project) => (
              <li key={project.slug}>
                <button type="button" className="home-work-row" onClick={() => open(project)}>
                  <span className="home-work-title">{project.title}</span>
                  <span className="home-work-line">{project.line}</span>
                  <span className="home-work-facts">
                    {(project.facts || project.skills.slice(0, 3)).join(' · ')}
                  </span>
                  <span className="home-work-org">{project.org}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {clients.length > 0 && (
          <div className="home-work">
            <h3 className="home-work-label">Client work</h3>
            <ul className="home-work-list">
              {clients.map((project) => (
                <li key={project.slug}>
                  <button type="button" className="home-work-row" onClick={() => open(project)}>
                    <span className="home-work-title">{project.title}</span>
                    <span className="home-work-line">{project.line}</span>
                    <span className="home-work-facts">
                      {(project.facts || project.skills.slice(0, 3)).join(' · ')}
                    </span>
                    <span className="home-work-org">{project.tag || 'Website'}</span>
                  </button>
                </li>
              ))}
            </ul>
            {featuredQuote && <Testimonial quote={featuredQuote} className="home-quote" />}
          </div>
        )}
      </section>

      <section className="home-section" aria-labelledby={sectionHeadingId('/media')}>
        <SectionHead to="/media" linkLabel="All media" />
        <div className="home-photos">
          {photos.map((item, i) => (
            <ShowcaseTile
              key={item.id}
              title={item.title}
              subtitle={item.location}
              img={item.src}
              onClick={() => setPhoto(i)}
            />
          ))}
        </div>
        {photo >= 0 && (
          <Suspense fallback={null}>
            <Lightbox
              open
              index={photo}
              close={() => setPhoto(-1)}
              slides={photos.map((item) => ({
                src: item.src,
                alt: item.title,
              }))}
            />
          </Suspense>
        )}
      </section>

      <section className="home-section" aria-labelledby={sectionHeadingId('/blog')}>
        <SectionHead to="/blog" linkLabel="All notes" />
        <ul className="home-posts">
          {latest.map((post) => (
            <li key={post.slug}>
              <TransitionLink to={`/blog/${post.slug}`} className="home-post">
                <span className="home-post-meta">
                  {post.cover && <img className="home-post-thumb" src={post.cover} alt="" loading="lazy" />}
                  <span>
                    {post.date} · {post.readTime}
                  </span>
                </span>
                <span className="home-post-title">{post.title}</span>
                <span className="home-post-excerpt">{post.excerpt}</span>
                <span className="home-post-go" aria-hidden="true">
                  <Arrow />
                </span>
              </TransitionLink>
            </li>
          ))}
        </ul>
      </section>

      <section className="home-section home-about" aria-labelledby={sectionHeadingId('/about')}>
        <SectionHead to="/about" linkLabel="Read more" />
        <div className="home-about-body">
          <div className="home-about-text">
            <p>
              I did a Bachelor's and Master's in civil engineering at the University of Alberta,
              then moved into software. Most recently I was a senior developer at Metrolinx,
              working on Azure infrastructure and CN integrations. Before that, two years of
              middleware for ONxpress on a $1.6B rail project, and a port to Next.js for a startup
              in Berkeley. I also shoot photos and video.
            </p>
          </div>
        </div>
      </section>

      {active && <ProjectSheet project={active} onClose={close} />}
    </div>
  );
}

export default Home;
